import { montantEffectif } from './pieces'

// Un PDF peut regrouper plusieurs factures (commande d'une place de marché : celle de la
// plateforme, puis une par vendeur). Le fichier reste entier, jamais retouché : chaque autre facture
// devient une pièce fille qui pointe vers lui avec sa plage de pages. Fonctions pures, sur le texte
// de chaque page ; dans le doute, on ne découpe pas (le fichier est lu d'un seul tenant).

export type FactureDuFichier = { numero: string; pageDebut: number; pageFin: number }

/**
 * Libellés du numéro propre d'une facture ou d'un avoir. « Facture originale », cité par un avoir,
 * n'en est pas un : le mot qui suit n'a pas la forme d'un numéro.
 */
const NUMERO_PROPRE = new RegExp(
  '(?:' +
    [
      'num[ée]ro\\s+de\\s+(?:la\\s+)?facture',
      "num[ée]ro\\s+de\\s+l['’]avoir",
      'n°\\s*(?:de\\s+)?facture',
      "n°\\s*(?:de\\s+l['’])?avoir",
      'facture\\s+n[°o]\\.?',
      'avoir\\s+n[°o]\\.?',
      'invoice\\s+(?:number|no\\.?|#)',
      'credit\\s+note\\s+(?:number|no\\.?|#)',
    ].join('|') +
    ')\\s*:?\\s*(\\S+)',
  'giu',
)

/** Forme d'un numéro : capitales et chiffres, au moins un chiffre. */
const FORME_NUMERO = /^[A-Z0-9][A-Z0-9._/-]{3,39}$/
/** « Page 2 de 3 », y compris tronqué (« Page 2 / », relevé bancaire). */
const COMPTEUR = /\bpage\s+(\d{1,3})\s*(?:de|sur|of|\/)/iu
/** Un document qui récapitule ou liste d'autres pièces cite leurs numéros : ce n'est pas une facture. */
const RECAPITULATIF = /relev[ée]s?\s+de\s+(?:comptes?|factures?|op[ée]rations?)/iu
/** Le total d'une facture : chacune a le sien. « Total » seul ne dit rien (total des opérations). */
const TOTAL_FACTURE =
  /total\s+(?:[àa]\s+payer|ttc)|facture\s+total|net\s+[àa]\s+payer|montant\s+ttc|avoir\s+total|invoice\s+total|amount\s+due/iu

function numerosDeLaPage(texte: string): Set<string> {
  const numeros = new Set<string>()
  for (const m of texte.matchAll(NUMERO_PROPRE)) {
    const numero = m[1]!.replace(/[.,;]+$/, '')
    if (FORME_NUMERO.test(numero) && /\d/.test(numero)) numeros.add(numero)
  }
  return numeros
}

/**
 * Les factures d'un fichier, d'après le texte de chaque page : un nouveau numéro ouvre une
 * nouvelle facture ; une page qui rappelle le même numéro (récapitulatif de TVA) ou n'en porte pas
 * reste avec la facture en cours ; celles d'avant le premier numéro vont avec la première.
 * Null : une seule facture, ou un doute (un relevé qui cite des factures, plusieurs numéros sur une
 * page, nouveau numéro au milieu d'un compteur de pages, numéro qui revient après un autre, facture
 * sans total à elle).
 */
export function frontieresFactures(pages: readonly string[]): FactureDuFichier[] | null {
  if (pages.some((texte) => RECAPITULATIF.test(texte))) return null
  const factures: FactureDuFichier[] = []
  for (const [i, texte] of pages.entries()) {
    const n = i + 1
    const numeros = numerosDeLaPage(texte)
    if (numeros.size > 1) return null
    const numero = [...numeros][0]
    const courante = factures.at(-1)
    if (!numero || numero === courante?.numero) {
      if (courante) courante.pageFin = n
      continue
    }
    if (!courante) {
      factures.push({ numero, pageDebut: 1, pageFin: n })
      continue
    }
    if (factures.some((f) => f.numero === numero)) return null
    const compteur = COMPTEUR.exec(texte)
    if (compteur && Number(compteur[1]) !== 1) return null
    factures.push({ numero, pageDebut: n, pageFin: n })
  }
  if (factures.length < 2) return null
  const chacuneSonTotal = factures.every((f) =>
    TOTAL_FACTURE.test(pages.slice(f.pageDebut - 1, f.pageFin).join('\n')),
  )
  return chacuneSonTotal ? factures : null
}

type Montants = {
  ht?: number | null
  tva?: number | null
  ttc?: number | null
  aPayer?: number | null
}

const somme = (valeurs: readonly (number | null | undefined)[]) =>
  valeurs.some((v) => v !== null && v !== undefined)
    ? valeurs.reduce<number>((s, v) => s + (v ?? 0), 0)
    : null

/**
 * Ce qu'un fichier de plusieurs factures demande de régler : la somme de ses factures. Le reste à
 * payer reste à zéro si toutes sont réglées ; sinon, il compte le montant effectif de chacune
 * (`montantEffectif` : à payer après remise, sinon TTC).
 */
export function totalDuFichier(montants: readonly Montants[]): Required<Montants> {
  const aPayer = montants.map((m) => m.aPayer)
  return {
    ht: somme(montants.map((m) => m.ht)),
    tva: somme(montants.map((m) => m.tva)),
    ttc: somme(montants.map((m) => m.ttc)),
    aPayer: aPayer.some((v) => v)
      ? somme(
          montants.map((m) =>
            montantEffectif({ ttcCts: m.ttc ?? null, aPayerCts: m.aPayer ?? null }),
          ),
        )
      : somme(aPayer),
  }
}

/**
 * Un fichier corrigé à la main, lu d'un seul tenant, qu'on découpe après coup : ce que disait la
 * correction décide. Le total du fichier (l'humain a contourné la lecture d'une seule facture) : il
 * reste le total, chaque facture est relue. Sa première facture : elle est gardée. Autre chose :
 * une correction ne se devine pas, un humain décide, et le fichier reste entier.
 */
export function suiteDuFichierCorrige(e: {
  corrigeCts: number | null
  /** Le total à payer lu dans le texte de chaque facture du fichier. */
  facturesCts: readonly (number | null)[]
}): 'relire' | 'garder' | 'laisser' {
  if (e.corrigeCts === null || e.facturesCts.some((m) => m === null)) return 'laisser'
  const total = e.facturesCts.reduce<number>((s, m) => s + (m ?? 0), 0)
  if (e.corrigeCts === total) return 'relire'
  return e.corrigeCts === e.facturesCts[0] ? 'garder' : 'laisser'
}

/**
 * Ce que le texte d'une facture de place de marché dit d'elle, pour un rapport : le vendeur et le
 * total à payer. Une prévision, jamais une lecture : la pièce, elle, est lue par le circuit normal.
 */
export function apercuFacture(texte: string): { vendeur: string | null; totalCts: number | null } {
  const vendeur = /vendu par\s+([^\n]+)/iu.exec(texte)?.[1]?.trim() ?? null
  const total = /total à payer\s+(-?\d[\d\s]*,\d{2})\s*€/iu.exec(texte)
  const facture = /facture total\s+(-?\d[\d\s]*,\d{2})\s*€/iu.exec(texte)
  const brut = (total ?? facture)?.[1]
  if (!brut) return { vendeur, totalCts: null }
  const negatif = brut.startsWith('-')
  const [euros, centimes] = brut.replace(/[-\s]/g, '').split(',')
  const cts = Number(euros) * 100 + Number(centimes)
  return { vendeur, totalCts: negatif ? -cts : cts }
}
